import { IRefreshAuthToken } from '../../store/ducks/auth/types';
import {
  ECustomerTypeView,
  EResearchNps,
  IUser,
  IUserPreference
} from '../../store/ducks/user/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { RefreshAuthTokenService } from '../Auth';
import { EGerdauService } from '../EGerdauService';
import { setCommerceTokenAvailable } from '../../store/ducks/auth/actions';
import store from '../../store';

export interface IUserService {
  getUser: (userId: string) => Promise<IUser>;
}

type SurveyProps = {
  status: EResearchNps;
  rating?: string;
  surveyText?: string | undefined;
};

export class UserService extends EGerdauService implements IUserService {
  async validate(): Promise<IUser> {
    try {
      const refreshAuthTokenService = new RefreshAuthTokenService();
      refreshAuthTokenService.checkAndRefreshToken();

      const headers = super.getHeaders();
      const access_token = super.getToken();

      if (!access_token) {
        throw new Error('Não foi encontrado o @access_token');
      }

      const validateResponse = await customersAuthorizationApi.post(
        `${ECustomerAuthorizationEndpoints.VALIDATE}`,
        { access_token },
        { headers }
      );

      const user: IUser = {
        ...validateResponse.data.user,
        expecialSteel: validateResponse.data.expecialSteel
      };

      const { dispatch } = store;
      if (validateResponse.data.commerce_token) {
        EGerdauService.setCommerceToken(validateResponse.data.commerce_token);
        dispatch(setCommerceTokenAvailable(true));
      } else {
        dispatch(setCommerceTokenAvailable(false));
      }

      return user;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  async getUser(userId: string): Promise<IUser> {
    const headers = super.getHeaders();
    const userResponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.USERS}/${userId}`,
      { headers }
    );

    const user: IUser = userResponse.data.user;
    if (!user.searchCustomerType) {
      // user = await this.saveUser(user, undefined, undefined, undefined, undefined, ECustomerTypeView.EMITTER);
      user.searchCustomerType = ECustomerTypeView.EMITTER;
    }
    return user;
  }

  async saveUser(
    user: IUser,
    userPreference?: IUserPreference,
    sendOrdersToEmail?: boolean,
    sendFinancialToEmail?: boolean,
    userAcceptNotification?: boolean,
    searchCustomerType?: ECustomerTypeView,
    improvementResearch?: boolean,
    privacyTerms?: boolean,
    userName?: string,
    improvementResearchNps?: EResearchNps,
    jorneySurveyShowcaseStatus?: EResearchNps,
    jorneySurveyHistoryStatus?: EResearchNps,
    jorneySurveyItemsStatus?: EResearchNps,
    jorneySurveySheetStatus?: EResearchNps,
    gerdauProModal?: boolean,
    modalsToSkip?: string[]
  ): Promise<IUser> {
    const headers = super.getHeaders();

    const userResponse = await customersAuthorizationApi.patch(
      `${ECustomerAuthorizationEndpoints.USERS}/${user.id}`,
      {
        user: {
          modalsToSkip: modalsToSkip,
          name: userName,
          preferences: userPreference ? [userPreference] : undefined,
          sendOrdersToEmail,
          sendFinancialToEmail,
          searchCustomerType,
          userAcceptNotification,
          ...([
            improvementResearchNps,
            jorneySurveyShowcaseStatus,
            jorneySurveyHistoryStatus,
            jorneySurveyItemsStatus,
            jorneySurveySheetStatus
          ].filter(Boolean).length > 0 && {
            userSurveys: {
              ...(improvementResearchNps && {
                userNps: {
                  improvementSurveyNps: improvementResearchNps
                }
              }),
              ...(jorneySurveyShowcaseStatus && {
                newJorneySurveyShowcase: {
                  status: jorneySurveyShowcaseStatus
                }
              }),
              ...(jorneySurveyHistoryStatus && {
                newJorneySurveyHistory: {
                  status: jorneySurveyHistoryStatus
                }
              }),
              ...(jorneySurveyItemsStatus && {
                newJorneySurveyItems: {
                  status: jorneySurveyItemsStatus
                }
              }),
              ...(jorneySurveySheetStatus && {
                newJorneySurveySheet: {
                  status: jorneySurveySheetStatus
                }
              })
            }
          }),
          ...([privacyTerms, improvementResearch, gerdauProModal].filter(
            (term) => term
          ).length > 0 && {
            terms: {
              privacyTerms,
              improvementResearch,
              gerdauProModal
            }
          })
        }
      },
      { headers }
    );

    const newUser: IUser = userResponse.data.user;

    return newUser;
  }

  async createPassword(
    userId: string,
    password: string,
    userAcceptNotification: boolean
  ) {
    await this.refreshTokenAzure();
    const headers = this.getHeadersNonLoggedInArea();

    const bodyRequest = {
      firstAuthentication: userId,
      password,
      userAcceptNotification
    };

    try {
      await customersAuthorizationApi.post(
        ECustomerAuthorizationEndpoints.CREATE_PASSWORD,
        { ...bodyRequest },
        { headers }
      );
    } catch (err) {
      console.error(err);
      throw new Error('Não foi possível criar a senha.');
    }
  }

  async refreshTokenAzure() {
    const refreshAuthTokenService = new RefreshAuthTokenService();
    const response: IRefreshAuthToken =
      await refreshAuthTokenService.refreshAuthToken();
    RefreshAuthTokenService.setAuthorizationToken(response.access_token);
  }

  async userLogout() {
    try {
      const headers = super.getHeaders();
      await customersAuthorizationApi.post(
        ECustomerAuthorizationEndpoints.LOGOUT,
        { token: super.getToken() },
        { headers }
      );
    } catch (error) {
      console.error('Error logging out', error);
      throw error;
    } finally {
      // Clean cookies
      // GR-600
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;

        document.cookie =
          name.trim() + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      }

      super.logout();
    }
  }

  static async sendNPS(
    rating?: string,
    mhsRating?: string,
    npsText?: string | undefined
  ) {
    const headers = new UserService().getHeaders();

    const body = {
      improvementSurveyNps: 'answered',
      npsRating: rating ?? '',
      npsText: npsText ?? '',
      mhsRating: mhsRating ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_NPS,
      body,
      { headers }
    );
  }
  static async sendSurveySheet({ status, rating, surveyText }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_SHEET,
      body,
      { headers }
    );
  }
  static async sendSurveySelectingItems({
    status,
    rating,
    surveyText
  }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_SELECTING_ITEMS,
      body,
      { headers }
    );
  }
  static async sendSurveyHistory({ status, rating, surveyText }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_HISTORY,
      body,
      { headers }
    );
  }
  static async sendSurveyShowCase({ status, rating, surveyText }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_SHOWCASE,
      body,
      { headers }
    );
  }
}
